// src/components/Sales/SalesCalculation.tsx

import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Tooltip,
    useMediaQuery,
    useTheme,
    Collapse
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SalesCalculateRequest, SalesCalculateResponse, PreCheckResponse } from '../../types/sales';
import { useUser } from '../../hooks/useUser';  // ユーザー情報取得用のフック
import { useAuth } from 'react-oidc-context';
import { salesService } from '../../services/salesService';

interface SalesCalculationProps {
    onCalculationComplete: (result: SalesCalculateResponse) => void;
}

export const SalesCalculation: React.FC<SalesCalculationProps> = ({
    onCalculationComplete
}) => {
    const { userInfo } = useUser();
    const auth = useAuth();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    // 状態管理
    const [targetMonth, setTargetMonth] = useState<string>(getCurrentMonth());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [monthExists, setMonthExists] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showResultDialog, setShowResultDialog] = useState(false);
    const [calculationResult, setCalculationResult] = useState<SalesCalculateResponse | null>(null);
    const [checkingMonth, setCheckingMonth] = useState(false);
    const [showInfo, setShowInfo] = useState(true);

    // 集計前チェック（未完了請求の確認）の状態
    const [preCheckResult, setPreCheckResult] = useState<PreCheckResponse | null>(null);
    const [showPreCheckDialog, setShowPreCheckDialog] = useState(false);

    // 現在の月を YYYY-MM 形式で取得
    function getCurrentMonth(): string {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }

    // 対象月が変更されたときに既存データの有無をチェック
    useEffect(() => {
        if (targetMonth) {
            checkExistingData();
        }
    }, [targetMonth]);

    // 対象月のデータ存在チェック
    const checkExistingData = async () => {
        if (!targetMonth) return;

        setCheckingMonth(true);
        setError(null);
        try {
            const token = auth.user?.access_token;
            const exists = await salesService.checkMonthExists(targetMonth, token);
            setMonthExists(exists);
        } catch (err) {
            console.error('売上データの確認に失敗しました:', err);
        } finally {
            setCheckingMonth(false);
        }
    };

    // 集計ボタンクリック時: まず未完了請求のチェックを行う
    const handleCalculate = async () => {
        const token = auth.user?.access_token;
        const preCheck = await salesService.preCheckCalculation(targetMonth, token);

        if (preCheck && preCheck.incompleteCount > 0) {
            // 未完了の請求依頼が残っている場合は警告ダイアログを表示
            setPreCheckResult(preCheck);
            setShowPreCheckDialog(true);
            return;
        }

        // 未完了なし（またはチェック不能）の場合は従来フローへ
        proceedAfterPreCheck();
    };

    // 未完了チェック通過後（または警告を承知で続行した後）の処理
    const proceedAfterPreCheck = () => {
        if (monthExists) {
            // 既存データが存在する場合は確認ダイアログを表示
            setShowConfirmDialog(true);
        } else {
            // 存在しない場合は直接実行
            executeCalculation();
        }
    };

    // 未完了警告ダイアログで「このまま集計する」がクリックされた時
    const handleProceedWithIncomplete = () => {
        setShowPreCheckDialog(false);
        proceedAfterPreCheck();
    };

    // 確認ダイアログでOKボタンがクリックされた時
    const handleConfirmCalculation = () => {
        setShowConfirmDialog(false);
        executeCalculation();
    };

    // 集計実行
    const executeCalculation = async () => {
        if (!targetMonth) {
            setError('対象月を選択してください');
            return;
        }

        // LastName プロパティを使用
        if (!userInfo?.lastName) {
            setError('ユーザー情報が取得できません。再ログインしてください。');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // 集計リクエスト - LastName プロパティを使用
            const request: SalesCalculateRequest = {
                targetMonth: targetMonth,
                userLastName: userInfo.lastName  // 正しいプロパティ名を使用
            };

            const token = auth.user?.access_token;
            const result = await salesService.calculateSales(request, token);

            // 集計結果を保存
            setCalculationResult(result);
            setShowResultDialog(true);

            // 親コンポーネントに結果を通知
            onCalculationComplete(result);
        } catch (err: any) {
            console.error('売上集計の実行に失敗しました:', err);
            setError(err.message || '売上集計の実行に失敗しました');
        } finally {
            setLoading(false);
        }
    };


    // 結果ダイアログを閉じる
    const handleCloseResultDialog = () => {
        setShowResultDialog(false);
    };

    return (
        <>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" component="h2">売上集計</Typography>
                    {!isSmallScreen && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => setShowInfo(!showInfo)}
                        >
                            {showInfo ? '説明を隠す' : '説明を表示'}
                        </Button>
                    )}
                </Box>

                <Collapse in={showInfo || isSmallScreen}>
                    <Alert
                        severity="info"
                        icon={<InfoIcon />}
                        sx={{ mb: 3 }}
                    >
                        <Typography variant="body2">
                            売上集計機能では、選択した月の工事データに基づいて売上と原価を計算し、
                            粗利を算出します。集計は経理担当者のみが実行できます。
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>集計時の注意事項:</strong>
                        </Typography>
                        <Typography variant="body2" component="ul" sx={{ pl: 2, mt: 0.5 }}>
                            <li>既に集計済みの月を選択した場合、以前のデータは上書きされます</li>
                            <li>諸経費・外注費未入力の工事は計上月がずれる場合があります</li>
                            <li>職長手当は基本利益の5%として計算されます</li>
                        </Typography>
                    </Alert>
                </Collapse>

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label="集計対象月"
                            type="month"
                            value={targetMonth}
                            onChange={(e) => setTargetMonth(e.target.value)}
                            fullWidth
                            required
                            InputLabelProps={{ shrink: true }}
                            disabled={loading}
                            error={!!error && !targetMonth}
                            helperText={!!error && !targetMonth ? '対象月を選択してください' : ''}
                            InputProps={{
                                endAdornment: checkingMonth ? <CircularProgress size={20} /> : null
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={8}>
                        {monthExists && (
                            <Alert
                                severity="warning"
                                icon={<WarningIcon />}
                                sx={{ mb: { xs: 2, sm: 0 } }}
                            >
                                選択した月の売上データは既に存在します。再集計すると上書きされます。
                            </Alert>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CalculateIcon />}
                                onClick={handleCalculate}
                                disabled={loading || checkingMonth || !targetMonth}
                                sx={{ minWidth: 120 }}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                                        集計中...
                                    </>
                                ) : '売上集計実行'}
                            </Button>
                        </Box>
                    </Grid>

                    {error && (
                        <Grid item xs={12}>
                            <Alert severity="error">{error}</Alert>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* 未完了請求の警告ダイアログ */}
            {preCheckResult && (
                <Dialog
                    open={showPreCheckDialog}
                    onClose={() => setShowPreCheckDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                        未完了の請求依頼があります
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        <Typography gutterBottom>
                            {targetMonth.replace('-', '年')}月の請求依頼のうち、
                            <strong>{preCheckResult.incompleteCount}件</strong>
                            がまだ「作成済み」になっていません。
                            このまま集計すると、これらの工事は売上に計上されません。
                        </Typography>
                        <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, my: 1 }}>
                            {preCheckResult.incompleteInvoices.map((inv) => (
                                <ListItem key={inv.ID}>
                                    <ListItemIcon>
                                        <WarningIcon color="warning" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`${inv.Quotation_No}　${inv.Customer_Name}`}
                                        secondary={`${inv.Site_Name}（担当: ${inv.Superviser}）`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        <Typography variant="body2" color="textSecondary">
                            可能であれば、請求書作成依頼ページで上記を「作成済み」にしてから集計してください。
                            なお、集計後に「作成済み」にした場合も売上データは自動作成されますが、
                            費用が未確定の工事は保留となり「要対応」タブに表示されます。
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setShowPreCheckDialog(false)}
                            color="primary"
                            variant="contained"
                        >
                            キャンセル（先に作成済みにする）
                        </Button>
                        <Button
                            onClick={handleProceedWithIncomplete}
                            color="warning"
                        >
                            このまま集計する
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* 再集計確認ダイアログ */}
            <Dialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                    確認
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography>
                        {targetMonth.replace('-', '年')}月の売上データは既に存在します。
                        再集計すると既存のデータが上書きされますが、よろしいですか？
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowConfirmDialog(false)}
                        color="inherit"
                    >
                        キャンセル
                    </Button>
                    <Button
                        onClick={handleConfirmCalculation}
                        color="warning"
                        variant="contained"
                    >
                        再集計する
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 集計結果ダイアログ */}
            {calculationResult && (
                <Dialog
                    open={showResultDialog}
                    onClose={handleCloseResultDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                        集計完了
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {targetMonth.replace('-', '年')}月の売上集計が完了しました
                            </Typography>

                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="集計された工事数"
                                        secondary={calculationResult.data.calculatedCount + "件"}
                                    />
                                </ListItem>

                                <Divider component="li" />

                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="状態"
                                        secondary="すべてのデータが「準備完了」状態になりました"
                                    />
                                </ListItem>
                            </List>

                            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                売上一覧で内容を確認し、問題がなければ「確定」してください。
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseResultDialog} color="primary" variant="contained">
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};